import { create } from 'zustand';
import type { JoinMeetingOptions } from '../types/meeting';
import type { MeetingStoreState } from './meetingStoreTypes';
import { createRoomAction, joinRoomAction, leaveRoomAction } from './meetingStoreRoomActions';
import {
  clearErrorAction,
  flipCameraAction,
  startScreenShareAction,
  stopScreenShareAction,
  toggleCameraAction,
  toggleMuteAction,
} from './meetingStoreMediaActions';

interface MeetingStore extends MeetingStoreState {
  createRoom: (roomName: string, hostUserId: string) => Promise<string>;
  joinRoom: (options: JoinMeetingOptions) => Promise<void>;
  leaveRoom: () => Promise<void>;
  toggleMute: () => void;
  toggleCamera: () => void;
  flipCamera: () => Promise<void>;
  startScreenShare: () => Promise<void>;
  stopScreenShare: () => Promise<void>;
  clearError: () => void;
  setSpotlight: (participantId: string | null) => void;
}

/**
 * Meeting state management store.
 */
export const useMeetingStore = create<MeetingStore>((set, get) => ({
  room: null,
  participants: [],
  localParticipant: null,
  isConnected: false,
  isConnecting: false,
  isSharingScreen: false,
  screenShareOwnerId: null,
  screenShareTrack: null,
  roomCameraTrack: null,
  roomCameraOwnerId: null,
  spotlightParticipantId: null,
  error: null,
  cameraDevices: [],
  currentCameraIndex: 0,

  /** Creates a new room in Firestore and stores it locally. */
  createRoom: createRoomAction(set),
  /** Joins a room via LiveKit and sets local participant state. */
  joinRoom: joinRoomAction(set, get),
  /** Leaves the current meeting and clears state. */
  leaveRoom: leaveRoomAction(set),
  /** Toggles the local microphone mute state. */
  toggleMute: toggleMuteAction(set, get),
  /** Toggles the local camera state. */
  toggleCamera: toggleCameraAction(set, get),
  /** Flips to the next available camera. */
  flipCamera: flipCameraAction(set, get),
  /** Starts screen sharing for the local participant. */
  startScreenShare: startScreenShareAction(set),
  /** Stops the active screen share. */
  stopScreenShare: stopScreenShareAction(set),
  /** Clears the last error. */
  clearError: clearErrorAction(set),
  /** Sets or clears the spotlighted participant. */
  setSpotlight: (participantId: string | null) => {
    const current = get().spotlightParticipantId;
    // Toggle off if clicking same participant
    set({ spotlightParticipantId: current === participantId ? null : participantId });
  },
}));

/**
 * Expose store on window for pop-out window access.
 */
if (typeof window !== 'undefined') {
  (window as Window & { __MEETING_STORE__?: typeof useMeetingStore }).
    __MEETING_STORE__ = useMeetingStore;
}
